/**
 * Application search box for the landing page.
 *
 * Rendered for EVERY signed-in role. There is deliberately no permission check
 * here: what a user can find is already determined by the module list handed
 * to it, so gating the control itself would only hide a feature end users are
 * meant to have.
 */

import { useState } from 'react';
import { Search, X } from 'lucide-react';

interface Props {
  value: string;
  onChange: (value: string) => void;
  /** Result count, shown once the user has typed. */
  resultCount: number;
  totalCount: number;
  className?: string;
}

export default function ApplicationSearch({
  value,
  onChange,
  resultCount,
  totalCount,
  className = '',
}: Props) {
  const searching = value.trim() !== '';

  /* Read-only until the user actually reaches for it.
     Browsers and password managers skip read-only fields when they autofill,
     and unlike `autocomplete="off"` that is not a hint they are free to
     ignore. Asking nicely was not enough here: the box still arrived holding
     the signed-in user's email. The field unlocks on the first focus or
     pointer-down, so typing is unaffected -- by the time a keystroke can
     land, the lock is already off. */
  const [locked, setLocked] = useState(true);
  const unlock = () => setLocked(false);

  return (
    <div className={`relative ${className}`}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        value={value}
        onChange={(event) => {
          /* Nothing may write here until the user has touched the field.
             This is the backstop for a manager that fills a read-only input
             anyway. Dropping the event is not enough on its own: the DOM node
             would keep the text it was given while React's state stayed
             empty, and with no state change there is no re-render to correct
             it -- the stale value would simply sit there. So put the node
             back to what this component believes it holds. */
          if (locked) {
            event.currentTarget.value = value;
            return;
          }
          onChange(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onChange('');
        }}
        readOnly={locked}
        onFocus={unlock}
        onMouseDown={unlock}
        onTouchStart={unlock}
        type="search"
        placeholder="Search applications…"
        aria-label="Search applications"
        /* NOT A CREDENTIAL FIELD, SAID IN EVERY DIALECT.
           Signing in and then landing here put the user's own email in this
           box, filtered the list by it, and left them looking at "no
           applications found". Nothing in this app ever writes to it — the
           state starts empty — so the value could only have come from a
           password manager: after a sign-in it looks for somewhere to put the
           username, and an unnamed text input with no autocomplete hint is
           exactly what its heuristics settle on.

           `name` and `id` matter as much as `autoComplete`, because the
           heuristics read those too; a field called "application-search" is
           not one they mistake for a login. The `data-` attributes opt out of
           the three third-party managers that ignore `autoComplete="off"`
           (LastPass, 1Password, Dashlane). Marking the sign-in form's own
           fields properly — see LoginScreen — is the other half: a manager
           that knows where the username belongs stops hunting for it. */
        name="application-search"
        id="application-search"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        data-lpignore="true"
        data-1p-ignore=""
        data-form-type="other"
        className="gl-input w-full py-2.5 pl-9 pr-9 text-sm"
        style={{
          // The landing page is dark; the default light-glass input would be
          // invisible there, so the tokens are inverted for this surface only.
          background: 'rgba(255,255,255,0.10)',
          borderColor: 'rgba(148,197,255,0.28)',
          color: '#F8FAFC',
        }}
      />
      {searching && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear application search"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-300 transition hover:bg-white/10 hover:text-white"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {searching && (
        <p
          className="absolute left-1 top-full mt-1 text-[11px]"
          style={{ color: 'rgba(203,225,250,0.7)' }}
          role="status"
        >
          {resultCount} of {totalCount} application{totalCount === 1 ? '' : 's'}
        </p>
      )}
    </div>
  );
}
